import Enumerable from "linq";
import { useCallback } from "react";
import { useQuery } from "react-query";
import i18n from "../../services/localization-service/i18n";
import { RestError } from "../../services/rest-service/RestService";
import { IUICustomizationPresetDescriptionDTO } from "../../services/ui-customization-data-service/Models";
import UICustomizationDataService from "../../services/ui-customization-data-service/UICustomizationDataService";
import { BaseUseQueryParameters } from "../BaseUseQueryParameters";
import { keys } from "./Keys";

export enum UICustomizationPresetSortEnum {
    NameAsc = 0,
    NameDesc = 1,
    DescriptionAsc = 2,
    DescriptionDesc = 3
}

export type UseUICustomizationPresetOptionsType = {
    filterText?: string;
    sortBy?: UICustomizationPresetSortEnum;
};

export type UseUICustomizationPresetParameters = {
    groupID: number;
    organizationID: number;
    options?: UseUICustomizationPresetOptionsType;
} & BaseUseQueryParameters<UseUICustomizationPresetReturnType>;

export type UseUICustomizationPresetReturnType = {
    data: Array<IUICustomizationPresetDescriptionDTO>;
    totalCount: number;
    filteredCount: number;
};

export function useUICustomizationPresets({
    groupID,
    organizationID,
    options,
    enabled,
    onError,
    onSuccess
}: UseUICustomizationPresetParameters) {
    return useQuery<Array<IUICustomizationPresetDescriptionDTO>, RestError, UseUICustomizationPresetReturnType>(
        keys.presetList(organizationID),
        ({ signal }) => {
            return UICustomizationDataService.getUICustomizationPresets(groupID, organizationID, signal);
        },
        {
            select: useCallback(
                (data: Array<IUICustomizationPresetDescriptionDTO>) => selectPresets(data, options),
                [options]
            ),
            enabled,
            onError,
            onSuccess
        }
    );
}

function selectPresets(data: Array<IUICustomizationPresetDescriptionDTO>, options?: UseUICustomizationPresetOptionsType): UseUICustomizationPresetReturnType {
    const presets = data.map(r => {
        return {
            ...r,
            name: r.name?.length > 0
                ? r.name
                : i18n.t("noName"),
            description: (r.description?.length ?? 0) > 0
                ? r.description
                : i18n.t("noDescription"),
        };
    });
    const filterText = options?.filterText?.toLowerCase() ?? "";

    // Filter presets
    let query = filterText && filterText.length > 0
        ? Enumerable
            .from(presets)
            .where(preset => {
                return (preset.name?.toLowerCase().indexOf(filterText) ?? -1) !== -1 ||
                    (preset.description?.toLowerCase().indexOf(filterText) ?? -1) !== -1;
            })
        : Enumerable.from(presets);

    // Sort presets
    switch (options?.sortBy) {
        case UICustomizationPresetSortEnum.NameAsc:
            query = query
                .orderBy(r => r.name?.toLowerCase())
                .thenBy(r => r.description?.toLowerCase());
            break;

        case UICustomizationPresetSortEnum.NameDesc:
            query = query
                .orderByDescending(r => r.name?.toLowerCase())
                .thenBy(r => r.description?.toLowerCase());
            break;

        case UICustomizationPresetSortEnum.DescriptionAsc:
            query = query
                .orderBy(r => r.description?.toLowerCase())
                .thenBy(r => r.name?.toLowerCase());
            break;

        case UICustomizationPresetSortEnum.DescriptionDesc:
            query = query
                .orderByDescending(r => r.description?.toLowerCase())
                .thenBy(r => r.name?.toLowerCase());
            break;
    }

    const filteredAndSortedPresets = query.toArray();

    return {
        data: filteredAndSortedPresets,
        totalCount: presets.length,
        filteredCount: filteredAndSortedPresets.length
    };
}